<template>
  <div v-bind:class="['event', e.right ? `event--${e.rightType}` : 'event--no-right', e.reverse ? 'event--reverse' : '', eventClass]">
    <TimelineEvent />
    <div>
      <div v-if="e.top" class="event__top">
        <template v-if="isTopYoutubeVideo">
          <YoutubePlayer v-bind:url="e.top" />
        </template>
        <template v-else>
          <a class="magn-link" v-bind:href="e.top.full">
            <Image v-bind:url="e.top.thumbnail" v-bind:alt="e.top.alt" />
          </a>
        </template>
      </div>
      <div class="event__detail">
        <div class="event__detail__left">
          <div class="event__detail__left__date">
            <StarIcon
              v-if="event.pinned"
              star-icon-class="event__detail__left__date__star-icon"
            />
            {{ e.date }}
          </div>
          <div class="event__detail__left__title">
            {{ e.title }}
          </div>
          <ToggleableText
            v-if="e.description"
            toggleable-text-class="event__detail__left__description"
            v-bind:text="e.description"
          />
        </div>
        <div v-if="e.right" class="event__detail__right">
          <div v-if="e.rightType === 'images' && Array.isArray(e.right)" class="event__detail__right__images">
            <template v-for="(image) in e.right">
              <a class="magn-link" v-bind:href="image.full">
                <Image v-bind:url="image.thumbnail" v-bind:alt="image.alt" />
              </a>
            </template>
          </div>
          <div v-if="e.rightType === 'quote'" class="event__detail__right__quote">
            <svg width="30" height="26" viewBox="0 0 30 26" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M6.768 25.1335C4.512 25.1335 2.808 24.3175 1.656 22.6855C0.552 21.0055 0 18.6775 0 15.7015C0 12.2935 0.84 9.29346 2.52 6.70146C4.2 4.10946 6.768 1.97346 10.224 0.293457L12.528 4.90146C10.368 6.00546 8.736 7.27746 7.632 8.71746C6.576 10.1095 6.048 11.8855 6.048 14.0455C6.192 13.9975 6.432 13.9735 6.768 13.9735C8.304 13.9735 9.6 14.4535 10.656 15.4135C11.76 16.3255 12.312 17.5975 12.312 19.2295C12.312 21.0535 11.784 22.4935 10.728 23.5495C9.672 24.6055 8.352 25.1335 6.768 25.1335ZM23.76 25.1335C21.504 25.1335 19.8 24.3175 18.648 22.6855C17.544 21.0055 16.992 18.6775 16.992 15.7015C16.992 12.2935 17.832 9.29346 19.512 6.70146C21.192 4.10946 23.76 1.97346 27.216 0.293457L29.52 4.90146C27.36 6.00546 25.728 7.27746 24.624 8.71746C23.568 10.1095 23.04 11.8855 23.04 14.0455C23.184 13.9975 23.424 13.9735 23.76 13.9735C25.296 13.9735 26.592 14.4535 27.648 15.4135C28.752 16.3255 29.304 17.5975 29.304 19.2295C29.304 21.0535 28.776 22.4935 27.72 23.5495C26.664 24.6055 25.344 25.1335 23.76 25.1335Z" fill="#80CE1B"/>
            </svg>
            {{ e.right }}
          </div>
          <div v-if="['image', 'image-small'].includes(e.rightType)" class="event__detail__right__image">
            <a class="magn-link" v-bind:href="e.right.full">
              <Image v-bind:url="e.right.thumbnail" v-bind:alt="e.right.alt" />
            </a>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Image from '@/components/Image';
import YoutubePlayer from '@/components/YoutubePlayer';
import { isYoutubeVideoUrl } from '@/utils/regexs';
import TimelineEvent from '@/components/TimelineEvent';
import StarIcon from '@/components/StarIcon';
import ToggleableText from '@/components/ToggleableText';

export const EventType = {
  date: {
    type: String,
    required: true,
  },
  title: {
    type: String,
    required: true,
  },
  description: {
    type: String,
    default: '',
  },
  reverse: {
    type: Boolean,
    default: false,
  },
  top: {
    type: [Boolean, String],
    default: false,
  },
  rightType: {
    type: String,
    default: 'images',
    validator: (v) => ['images', 'quote', 'image', 'image-small'].includes(v),
  },
  right: {
    type: [Boolean, Array, String],
    default: false,
  },
  pinned: {
    type: Boolean,
    default: false,
  }
}

export default {
  name: 'Event',
  props: {
    event: EventType,
    eventClass: String,
  },
  components: {
    ToggleableText,
    TimelineEvent,
    Image,
    YoutubePlayer,
    StarIcon,
  },
  computed: {
    e: function() {
      return {
        ...this.event,
        description: this.event.description || '',
        reverse: this.event.reverse || false,
        top: this.event.top || false,
        rightType: this.event.rightType || 'images',
        right: this.event.right || false,
        pinned: this.event.pinned || false,
      }
    },
    isTopYoutubeVideo: function () {
      return this.event.top && typeof this.event.top === 'string' && isYoutubeVideoUrl(this.event.top);
    },
  }
};
</script>

<style lang="scss">
  @import '@/styles/variables.scss';
  @import '@/styles/functions.scss';

  .event {
    color: white;
    margin-left: -8px;

    display: grid;
    grid-template-columns: 24px 1fr;
    grid-gap: 16px;
    align-items: center;

    img {
      width: 100%;
      border-radius: 6px;
    }

    &:last-child {
      margin-bottom: 0;
    }

    &__top {
      margin-bottom: 24px;
    }

    &__detail {
      display: flex;
      flex-direction: column;

      &__left {
        margin-bottom: 16px;

        &__date {
          color: $damiGrass;
          font-family: 'Roboto Condensed', sans-serif;
          font-weight: bold;
          font-size: 12px;
          line-height: 14px;
          letter-spacing: 0.3em;
          text-transform: uppercase;
          margin-bottom: 4px;
          display: flex;
          align-items: center;
          gap: 12px;
        }

        &__title {
          font-family: 'Zilla Slab', sans-serif;
          font-weight: bold;
          font-size: 24px;
          line-height: 29px;
        }

        &__description {
          font-size: 15px;
          line-height: 140%;
          margin-top: 24px;
        }
      }

      &__right {
        &__images {
          display: grid;
          grid-template-columns: repeat(4, 63px);
          grid-gap: 15px;

          .image {
            height: 63px;
            object-fit: cover;
          }
        }

        &__quote {
          font-weight: bold;
          font-size: 18px;
          line-height: 23px;
          color: $damiGrass;

          svg {
            display: block;
            margin-bottom: 12px;
            width: 30px;
            height: 26px;
          }
        }
      }
    }

    &--reverse {
      .event__detail {
        flex-direction: column-reverse;

        &__left {
          margin-bottom: 0;
        }

        &__right {
          margin-bottom: 16px;
        }
      }
    }

    &--image-small {
      .event__detail {
        flex-direction: row-reverse;

        &__right {
          margin-right: 24px;
          min-width: 68px;
          width: 68px;

          .image {
            height: 68px;
            object-fit: cover;
          }
        }
      }
    }

    &--image {
      .event__detail {
        &__left {
          margin-bottom: 24px;
        }
      }
    }

    &--image.event--reverse {
      .event__detail {
        &__left {
          margin-bottom: 0;
        }

        &__right {
          margin-bottom: 24px;
        }
      }
    }

    &--no-right {
      .event__detail {
        max-width: none;

        &__left {
          max-width: none;
          margin-bottom: 0;
        }
      }
    }

    @include media-breakpoint-up(md) {
      grid-template-columns: 46px 1fr;
      grid-gap: 42px;

      &__top {
        margin-bottom: 32px;

        img {
          border-radius: 20px;
        }
      }

      &__detail {
        max-width: 458px;

        &__left {
          margin-bottom: 32px;

          &__date {
            font-size: 16px;
            line-height: 19px;
          }

          &__title {
            font-size: 28px;
            line-height: 34px;
          }

          &__description {
            font-size: 16px;
            line-height: 150%;
          }
        }

        &__right {
          &__images {
            display: grid;
            grid-template-columns: repeat(4, 68px);
            grid-gap: 24px;

            .image {
              height: 68px;
            }
          }

          &__quote {
            font-size: 24px;
            line-height: 30px;
          }

          &__image {
            max-width: 380px;
            width: 380px;

            img {
              border-radius: 20px;
              width: 100%;
            }
          }
        }
      }

      &--image-small {
        .event__detail {
          max-width: none;
          justify-content: flex-end;

          &__right {
            margin-right: 32px;

            &__image {
              width: 68px;
              max-width: 68px;

              img {
                border-radius: 6px;
              }
            }
          }
        }
      }
    }

    @include media-breakpoint-up(lg) {
      grid-gap: 58px;

      &__detail {
        min-width: 100%;
        width: 100%;
        max-width: none;
        flex-direction: row;
        justify-content: space-between;

        &__left {
          max-width: 458px;
          margin-right: 16px;
          margin-bottom: 0;

          &__title {
            font-size: 32px;
            line-height: 38px;
          }

          &__description {
            font-size: 16px;
            line-height: 150%;
          }
        }

        &__right {
          margin-left: 16px;
        }
      }

      &--image-small {
        .event__detail {
          &__left {
            max-width: none;
            margin-right: 0;
          }

          &__right {
            margin-left: 0;
            margin-bottom: 0;
          }
        }
      }

      &--reverse {
        .event__detail {
          flex-direction: row-reverse;

          &__left {
            margin-left: 16px;
          }

          &__right {
            margin-right: 16px;
          }
        }
      }

      &--image {
        .event__detail {
          &__left {
            margin-bottom: 0;
            margin-right: 16px;
          }

          &__right {
            margin-left: 16px;
          }
        }
      }

      &--image.event--reverse {
        .event__detail {
          &__left {
            margin-right: 0;
            margin-left: 16px;
          }

          &__right {
            margin-left: 0;
            margin-right: 16px;
            margin-bottom: 0;
          }
        }
      }

      &--no-right {
        .event__detail {
          &__left {
            max-width: none;
            margin-right: 0;
          }
        }
      }
    }
  }
</style>
